import React, { useCallback, useContext, useEffect, useRef } from "react";
import { AppContext, DispatchContext } from "../AppContext";
import Paper from '@material-ui/core/Paper';
import { makeStyles } from "@material-ui/styles";
import * as actions from '../actions/actions';
import Service from "../services/service"
import CancelIcon from '@material-ui/icons/Cancel';
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';
import { ReactComponent as MagicWandIcon } from '../assets/icons/magic-wand.svg';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import CircularProgress from '@material-ui/core/CircularProgress';
import LabelWithTooltip from './LabelWithTooltip';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Tooltip from '@material-ui/core/Tooltip';
import { Link, Button } from '@material-ui/core';
import FileUploadPanel from './FileUploadPanel';
import FormGroup from '@material-ui/core/FormGroup';
import UrlTextField from "./urlTextField";
import { config, resolveNotificationMessage } from '../helpers/constants';
import { ReactComponent as BooksIcon } from '../assets/icons/books.svg';
import FileCard from "./FileCard";
import LoadingScreen from '../components/LoadingScreen';

/**
 * Merge overlapping or adjacent regions and sort them by start position.
 * Each region is { start, end } where start is inclusive and end is exclusive.
 */
function mergeRegions(regions) {
  if (regions.length === 0) return [];
  const sorted = [...regions].sort((a, b) => a.start - b.start);
  const merged = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1];
    if (sorted[i].start <= last.end) {
      last.end = Math.max(last.end, sorted[i].end);
    } else {
      merged.push({ ...sorted[i] });
    }
  }
  return merged;
}

/**
 * Render text with excluded regions shown in gray.
 * Returns an array of React elements (spans).
 */
function renderHighlightedText(text, excludeRegions) {
  if (!text || excludeRegions.length === 0) {
    return text || '';
  }
  const regions = mergeRegions(excludeRegions);
  const parts = [];
  let pos = 0;
  regions.forEach((region, idx) => {
    // Clamp to text length
    const start = Math.min(region.start, text.length);
    const end = Math.min(region.end, text.length);
    if (start > pos) {
      parts.push(<span key={`t${idx}`}>{text.substring(pos, start)}</span>);
    }
    if (end > start) {
      parts.push(<span key={`e${idx}`} style={{ color: '#bbbbbb' }}>{text.substring(start, end)}</span>);
    }
    pos = end;
  });
  if (pos < text.length) {
    parts.push(<span key="tail">{text.substring(pos)}</span>);
  }
  return parts;
}


export default function TextFieldPanel(props) {

  const useStyles = makeStyles((theme) => ({
    root: {
      overflow: "hidden",
      flexDirection: "column",
      display: "flex",
      padding: theme.spacing(1),
      lineHeight: '18px',
      height: "100%"
    },

    paperHeader: {
      flex: "0 0 auto",
      padding: theme.spacing(1),
      fontSize: "1rem"
    },

    paperContent: {
      flex: "1 1 auto",
      minHeight: 0,
      borderBottom: "1px solid #dddddd",
      position: "relative",
    },

    textarea: {
      background: "transparent",
      resize: "none",
      border: "0 none",
      width: "100%",
      outline: "none",
      height: "100%",
      fontFamily: "Roboto, Helvetica, Arial, sans-serif",
      fontSize: "14px",
      lineHeight: "20px",
      letterSpacing: "normal",
      whiteSpace: "pre-wrap",
      wordWrap: "break-word",
      padding: "4px",
      margin: 0,
      boxSizing: "border-box",
      overflow: "auto",
    },

    textareaOverlayContainer: {
      // unused — kept to avoid renaming references if reused later
    },

    exclusionDisplay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      fontFamily: "Roboto, Helvetica, Arial, sans-serif",
      fontSize: "14px",
      lineHeight: "20px",
      letterSpacing: "normal",
      whiteSpace: "pre-wrap",
      wordWrap: "break-word",
      overflow: "auto",
      padding: "4px",
      margin: 0,
      boxSizing: "border-box",
      cursor: "text",
      userSelect: "text",
      zIndex: 1,
    },

    exclusionCanvas: {
      position: "absolute",
      top: 0,
      left: 0,
      pointerEvents: "none",
      zIndex: 2,
    },


    borderColor: {
      width: "100%",
      "& .MuiInput-underline:before": {
        borderBottom: "0px"
      },
      "& .MuiInput-underline:after": {
        borderBottom: "0px"
      },
      "& .MuiInput-underline:not(.Mui-disabled):before": {
        borderBottom: "0px"
      },
    },
    cancelButton: {
      textAlign: "end"
    },
    BorderRadius: {
      borderRadius: ""
    },
    textFieldSize: {
      width: "100%",
      display: "flex"
    },
    textPadding: {
      paddingTop: "5px",
      paddingBottom: "5px"
    },
    loader: {
      textAlign: "center",
      color: "white",
    },
    overlay: {
      zIndex: "99",
      display: "flex",
      justifyContent: "center",
      position: "relative",
      backgroundColor: "rgba(0,0,0,0.1)",
      height: "100%",
      width: "100%",
      alignItems: "center",
    },
    publicIconPadding: {
      paddingTop: "7px",
      paddingRight: "7px"
    },
    helpIconPadding: {
      paddingLeft: "7px",
      paddingTop: "7px"
    },
    rowAlign: {
      display: "flex"
    },
    table_library_header: {
      width: '20%',
      minWidth: "50px",
    },
    table_credits_header: {
      width: '60px',
      textAlign: "center",
      cursor: "default",
    },
    libraryName: {
      padding: '10px 5px',
      textAlign: 'left',
    },
    librarySource: {
      width: '20%',
      padding: '10px 5px',
      textAlign: 'left',
    },
    activitywords: {
      width: '85px',
      padding: '10px 5px',
      textAlign: 'left',
    },
    tableRow: {
      "&:hover": {
        backgroundColor: '#eeeeee ',
      },
      borderTop: '1px solid #D3D3D3',
      "&:last-child": {
        borderBottom: '1px solid #D3D3D3',
      },
      "&:nth-child(even)": {
        backgroundColor: '#f3f3f3',
        "&:hover": {
          backgroundColor: '#eeeeee ',
        },
      }
    },
    selectTextFromOtherSource: {
      margin: "auto",
      textAlign: "center",
      maxWidth: "46%",
    },
    infoIcon: {
      fontSize: "45px"
    }
  }))

  const dispatch = useContext(DispatchContext);
  const state = useContext(AppContext);
  const s = state.strings;
  const classes = useStyles(state);
  const [localText, setLocalText] = React.useState('');
  const [url, setUrl] = React.useState('');
  const [considerTags, setConsiderTags] = React.useState(true);
  const { source, text, textChanged } = props;
  const [time, setTime] = React.useState({ typingTimeout: 0 });
  const [urlLoading, setUrlLoading] = React.useState(false);
  const [hasSelection, setHasSelection] = React.useState(false);
  const [exclusionMode, setExclusionMode] = React.useState(false);
  const [wizardMenuAnchor, setWizardMenuAnchor] = React.useState(null);
  const [wizardSections, setWizardSections] = React.useState([]);
  const [wizardLoading, setWizardLoading] = React.useState(false);
  const textareaRef = useRef(null);
  const exclusionDisplayRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const mouseAnchorRef = useRef(null);
  const isDraggingRef = useRef(false);
  const lastMousePosRef = useRef({ x: 0, y: 0 });
  const isCorrectingRef = useRef(false);
  const exclusionCanvasRef = useRef(null);
  const expression = /^(https?|chrome):\/\/[^\s$.?#].[^\s]*$/ig;
  const regex = new RegExp(expression);

  const excludeRegions = state.newCheckSettings.excludeRegions || [];

  useEffect(() => {
    if (text != localText) {
      setLocalText(text);
    }
  }, [text]);

  // Reset exclusion mode and clear regions when the source changes
  useEffect(() => {
    setExclusionMode(false);
    if (excludeRegions.length > 0) {
      dispatch(actions.setNewCheckSettings({ excludeRegions: [] }));
    }
  }, [source]);

  // While in exclusion mode, intercept selectionchange to immediately correct
  // any browser mis-selection (e.g. snap-to-end when mouse is between lines).
  // We track the latest mouse position from mousemove so we can call
  // caretFromPoint inside selectionchange where no coordinates are available.
  useEffect(() => {
    if (!exclusionMode) return;
    const onSelectionChange = () => {
      if (!isDraggingRef.current || !mouseAnchorRef.current || !exclusionDisplayRef.current) return;
      if (isCorrectingRef.current) return;
      const focus = caretFromPoint(lastMousePosRef.current.x, lastMousePosRef.current.y);
      if (!focus || !exclusionDisplayRef.current.contains(focus.node)) return;
      isCorrectingRef.current = true;
      try {
        window.getSelection().setBaseAndExtent(
          mouseAnchorRef.current.node, mouseAnchorRef.current.offset,
          focus.node, focus.offset
        );
      } catch (_) {}
      // Reset guard after the browser has processed our correction
      setTimeout(() => { isCorrectingRef.current = false; }, 0);
    };
    document.addEventListener('selectionchange', onSelectionChange);

    // Handle mouseup anywhere on the document so drags that start AND end
    // outside the exclusion div still register if the browser selection
    // crosses into the div.
    const onDocMouseUp = (e) => {
      // If the mouseup target is inside the div, the div's own onMouseUp
      // handler will fire — skip to avoid double processing.
      if (exclusionDisplayRef.current && exclusionDisplayRef.current.contains(e.target)) return;
      // Only act when the drag did not originate inside the div (anchor is null).
      if (mouseAnchorRef.current !== null) return;
      isDraggingRef.current = false;
      if (exclusionDisplayRef.current) {
        const selection = getSelectionInDiv(exclusionDisplayRef.current);
        if (selection) {
          savedSelectionRef.current = selection;
          setHasSelection(true);
          return;
        }
      }
      savedSelectionRef.current = null;
      setHasSelection(false);
    };
    document.addEventListener('mouseup', onDocMouseUp);

    return () => {
      document.removeEventListener('selectionchange', onSelectionChange);
      document.removeEventListener('mouseup', onDocMouseUp);
    };
  }, [exclusionMode]);

  // Wizard: call API to detect document sections, then show menu
  const openWizardMenu = (e) => {
    const anchor = e.currentTarget;
    if (!localText) return;
    setWizardLoading(true);
    setWizardSections([]);
    setWizardMenuAnchor(anchor);
    Service.detectDocumentSections(localText).then(res => {
      setWizardLoading(false);
      if (res.success && res.data && res.data.length > 0) {
        setWizardSections(res.data);
      } else {
        setWizardSections([]);
      }
    }).catch(() => {
      setWizardLoading(false);
      setWizardSections([]);
    });
  };

  // Convert a UTF-8 byte offset to a JS string char offset
  const byteOffsetToCharOffset = (str, byteOffset) => {
    let charIdx = 0;
    let byteCount = 0;
    while (charIdx < str.length && byteCount < byteOffset) {
      const cp = str.codePointAt(charIdx);
      if (cp < 0x80)        byteCount += 1;
      else if (cp < 0x800)  byteCount += 2;
      else if (cp < 0x10000) byteCount += 3;
      else                  byteCount += 4;
      charIdx += cp > 0xFFFF ? 2 : 1;
    }
    return charIdx;
  };

  // Wizard: exclude a section returned by the API
  const handleExcludeSection = (section) => {
    setWizardMenuAnchor(null);
    const byteStart = section.position?.start ?? section.Start ?? section.start;
    const byteEnd   = section.position?.end   ?? section.End   ?? section.end;
    const start = byteOffsetToCharOffset(localText, byteStart);
    const end   = byteOffsetToCharOffset(localText, byteEnd);
    if (end <= start) return;
    dispatch(actions.setNewCheckSettings({ excludeRegions: mergeRegions([...excludeRegions, { start, end }]) }));
    if (!exclusionMode) setExclusionMode(true);
  };

  // Draw excluded regions as gray overlays on the canvas
  const drawExclusionOverlay = useCallback(() => {
    const canvas = exclusionCanvasRef.current;
    const div = exclusionDisplayRef.current;
    if (!canvas || !div) return;
    const dpr = window.devicePixelRatio || 1;
    const w = div.clientWidth;
    const h = div.clientHeight;
    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
    }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    const regions = excludeRegions;
    if (!localText || regions.length === 0) return;
  }, [excludeRegions, localText]);

  // Redraw canvas whenever regions, text or scroll changes
  useEffect(() => {
    if (exclusionMode) drawExclusionOverlay();
  }, [exclusionMode, excludeRegions, localText, drawExclusionOverlay]);

  // Get character offset of a DOM node+offset within a container element
  const getCharOffset = (container, node, offset) => {
    if (node.nodeType !== Node.TEXT_NODE) {
      // caretRangeFromPoint returned an element node; offset is a child index.
      // Count all text characters up to (but not including) childNodes[offset].
      const refChild = node.childNodes[offset] || null;
      let total = 0;
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false);
      while (walker.nextNode()) {
        if (refChild && (refChild === walker.currentNode || refChild.contains(walker.currentNode))) break;
        total += walker.currentNode.textContent.length;
      }
      return total;
    }
    let total = 0;
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false);
    while (walker.nextNode()) {
      if (walker.currentNode === node) return total + offset;
      total += walker.currentNode.textContent.length;
    }
    return total;
  };

  // Reverse of getCharOffset: find the text node and intra-node offset for a given char offset
  const getNodeAtOffset = (container, targetOffset) => {
    let total = 0;
    let lastNode = null;
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false);
    while (walker.nextNode()) {
      lastNode = walker.currentNode;
      const len = walker.currentNode.textContent.length;
      if (total + len >= targetOffset) {
        return { node: walker.currentNode, offset: targetOffset - total };
      }
      total += len;
    }
    return lastNode ? { node: lastNode, offset: lastNode.textContent.length } : null;
  };

  // Returns { node, offset } for the caret position nearest to (x, y)
  const caretFromPoint = (x, y) => {
    if (document.caretRangeFromPoint) {
      const r = document.caretRangeFromPoint(x, y);
      if (r) return { node: r.startContainer, offset: r.startOffset };
    } else if (document.caretPositionFromPoint) {
      const p = document.caretPositionFromPoint(x, y);
      if (p) return { node: p.offsetNode, offset: p.offset };
    }
    return null;
  };

  // Read window.getSelection() and return {start, end} char offsets clamped to
  // the given div, or null if the selection doesn't intersect the div at all.
  // Uses range.intersectsNode so it works even when the drag started outside.
  const getSelectionInDiv = (div) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;
    const range = sel.getRangeAt(0);
    if (!range.intersectsNode(div)) return null;
    // Clamp endpoints to the div's content boundaries
    const divRange = document.createRange();
    divRange.selectNodeContents(div);
    const startNode   = range.compareBoundaryPoints(Range.START_TO_START, divRange) < 0 ? divRange.startContainer : range.startContainer;
    const startOffset = range.compareBoundaryPoints(Range.START_TO_START, divRange) < 0 ? divRange.startOffset    : range.startOffset;
    const endNode     = range.compareBoundaryPoints(Range.END_TO_END,   divRange) > 0 ? divRange.endContainer   : range.endContainer;
    const endOffset   = range.compareBoundaryPoints(Range.END_TO_END,   divRange) > 0 ? divRange.endOffset      : range.endOffset;
    const start = getCharOffset(div, startNode, startOffset);
    const end   = getCharOffset(div, endNode,   endOffset);
    return end > start ? { start, end } : null;
  };

  // Capture anchor on mouseDown and start tracking drag
  const handleMouseDown = (e) => {
    if (!exclusionMode || !exclusionDisplayRef.current) return;
    const caret = caretFromPoint(e.clientX, e.clientY);
    if (caret && exclusionDisplayRef.current.contains(caret.node)) {
      mouseAnchorRef.current = caret;
      isDraggingRef.current = true;
      lastMousePosRef.current = { x: e.clientX, y: e.clientY };
    } else {
      mouseAnchorRef.current = null;
      isDraggingRef.current = false;
    }
  };

  // Keep latest mouse coords so selectionchange can call caretFromPoint
  const handleMouseMove = (e) => {
    if (isDraggingRef.current) {
      lastMousePosRef.current = { x: e.clientX, y: e.clientY };
    }
  };

  // Finalise selection on mouseUp / keyUp
  const handleSelect = (e) => {
    if (exclusionMode && exclusionDisplayRef.current) {
      if (e && e.type === 'mouseup') {
        isDraggingRef.current = false;
        const anchor = mouseAnchorRef.current;
        mouseAnchorRef.current = null;
        if (anchor) {
          const focus = caretFromPoint(e.clientX, e.clientY);
          if (focus && exclusionDisplayRef.current.contains(focus.node)) {
            let a = getCharOffset(exclusionDisplayRef.current, anchor.node, anchor.offset);
            let f = getCharOffset(exclusionDisplayRef.current, focus.node, focus.offset);
            if (a > f) [a, f] = [f, a];
            if (f > a) {
              savedSelectionRef.current = { start: a, end: f };
              setHasSelection(true);
              return;
            }
          }
        }
        // anchor is null (drag started outside the div) or produced no valid range:
        // fall through to window.getSelection() to capture any browser-native selection
      }
      // Keyboard OR fallback for mouse drag that started outside the div:
      const selection = getSelectionInDiv(exclusionDisplayRef.current);
      if (selection) {
        savedSelectionRef.current = selection;
        setHasSelection(true);
        return;
      }
      savedSelectionRef.current = null;
      setHasSelection(false);
    } else if (textareaRef.current) {
      setHasSelection(textareaRef.current.selectionStart !== textareaRef.current.selectionEnd);
    }
  };

  // Exclude the currently selected text range
  const handleExcludeSelection = () => {
    let start, end;
    if (exclusionMode) {
      if (!savedSelectionRef.current) return;
      ({ start, end } = savedSelectionRef.current);
      savedSelectionRef.current = null;
    } else {
      if (!textareaRef.current) return;
      start = textareaRef.current.selectionStart;
      end   = textareaRef.current.selectionEnd;
      if (start === end) return;
    }
    if (start === end) return;
    // Preserve scroll position of the exclusion display
    const scrollTop = exclusionDisplayRef.current ? exclusionDisplayRef.current.scrollTop : 0;
    dispatch(actions.setNewCheckSettings({ excludeRegions: mergeRegions([...excludeRegions, { start, end }]) }));
    setHasSelection(false);
    // Restore scroll after React re-renders
    if (exclusionDisplayRef.current) {
      requestAnimationFrame(() => {
        if (exclusionDisplayRef.current) exclusionDisplayRef.current.scrollTop = scrollTop;
      });
    }
  };

  // Clear all excluded regions (with confirmation)
  const handleClearExcludedRegions = () => {
    dispatch(actions.openConfirmationDialog({
      title: s.newCheck.clearExcludedRegionsTitle,
      text: s.newCheck.clearExcludedRegionsText,
      onConfirm: () => {
        savedSelectionRef.current = null;
        setHasSelection(false);
        dispatch(actions.setNewCheckSettings({ excludeRegions: [] }));
      },
    }));
  };

  // Toggle exclusion mode; if turning off and regions exist, confirm first
  const handleExclusionModeToggle = (checked) => {
    if (!checked && excludeRegions.length > 0) {
      dispatch(actions.openConfirmationDialog({
        title: s.newCheck.editTextWithExclusionsTitle,
        text: s.newCheck.editTextWithExclusionsText,
        onConfirm: () => {
          dispatch(actions.setNewCheckSettings({ excludeRegions: [] }));
          setExclusionMode(false);
          setHasSelection(false);
          savedSelectionRef.current = null;
        },
      }));
    } else {
      setExclusionMode(checked);
      setHasSelection(false);
      savedSelectionRef.current = null;
    }
  };


  // set value of text field 
  const onTextChange = (value) => {
    if (value.length > config.textAllowed) {
      dispatch(actions.openToast({ type: "error", message: s.truncatedMessage, status: true }));
    }
    const truncatedValue = value.substring(0, config.textAllowed);
    setLocalText(truncatedValue);
    if (time.typingTimeout) {
      clearTimeout(time.typingTimeout);
    }
    let typingTime = setTimeout(function () {
      textChanged(truncatedValue);
    }, 500)
    setTime({ typingTimeout: typingTime })
  }

  // clear text field data 
  const clearTextField = () => {
    if (excludeRegions.length > 0) {
      dispatch(actions.setNewCheckSettings({ excludeRegions: [] }));
    }
    onTextChange('');
  }

  // set url in text field
  const onUrlFieldChange = (value) => {
    setUrl(value.trim());
  }

  // get text data from getUrlContent()
  const getTextFromUrl = () => {

    if (!url)
      return;

    onTextChange('');

    if (url.match(regex)) {
      setUrlLoading(true);
      Service.getUrlContents({ usetags: considerTags ? 1 : 0, url }).then(res => {
        setUrlLoading(false);
        if (res.data) {
          onTextChange(res.data.Text);
          if (res.data.Truncated) {
            dispatch(actions.openToast({ type: "error", message: s.truncatedMessage, status: true }));
          }
        }
        else {
          dispatch(actions.openToast({ type: "error", message: s.newCheck.urlReadError, status: true }));
        }
      });
    } else {
      dispatch(actions.openToast({ type: "error", message: s.invalidUrl, status: true }));
    }
  }


  // get data from getUrlContents on enter
  const getTextOnEnter = (event) => {
    if (event.key === "Enter") {
      document.getElementById("url").blur();
    }
  }

  //change tags checkbox value
  const tagsCheckboxClicked = (checked) => {
    setConsiderTags(checked);
  }


  const onDeleteLibraryText = useCallback((e, id) => {
    let remainingTexts = state.newCheckSettings.libraryTexts.filter(item => item.Id != id);
    dispatch(actions.setNewCheckSettings({ libraryTexts: remainingTexts }));
  });



  return (
    <Paper className={classes.root}>
      {source === 'upload' ? <FileUploadPanel showEdit={props.showEdit} /> :
        <>
          {source === "download" &&
            <div className={classes.paperHeader} style={{ borderBottom: "1px solid #dddddd" }}>
              <div className={classes.textFieldSize}>
                <UrlTextField onUrlFieldChange={onUrlFieldChange} getTextFromUrl={getTextFromUrl} urlFieldValue={url} getTextOnEnter={getTextOnEnter} />
              </div>
              <FormGroup style={{ width: "fit-content" }}>
                <FormControlLabel data-for="globalTT" data-tip={s.newCheck.considerPlagAwareTagsTip}
                  control={<Checkbox checked={considerTags} onChange={e => tagsCheckboxClicked(e.target.checked)} />}
                  label={s.newCheck.considerPlagAwareTags} />
              </FormGroup>
            </div>
          }
          <>
            {source === "library" ?
              <>
                {state.newCheckSettings.libraryTexts.length > 0 ?
                  <div style={{ width: "100%", minWidth: 0, minHeight: "100%", height: 0, overflow: "auto", display: "flex", flexWrap: "wrap", alignContent: "flex-start" }}>
                    {state.newCheckSettings.libraryTexts.map((item) => {
                      return (
                        <div style={{ width: "50%", padding: 8 }}>
                          <FileCard id={item.Id} title={item.Name} text1={s.newCheck.uploadFileWords.format(item.Words, item.Pages)} projectId={item.ProjectId} projectName={item.ProjectName} type="txt" onDelete={onDeleteLibraryText} progressValue={100} />
                        </div>
                      );
                    })}
                  </div>
                  :
                  <div className={classes.selectTextFromOtherSource}>
                    <BooksIcon onClick={() => dispatch(actions.navigate_to("/library/overview"))} fill="#0D4A81" height={100} width={100} style={{ cursor: "pointer", margin: 16 }} />
                    <p>
                      {s.newCheck.visitLibrary}
                    </p>
                    {s.common.goto} <Link href="#" onClick={() => dispatch(actions.navigate_to("/library/overview"))}>{s.library}</Link>
                  </div>
                }
              </>
              :
              <>
                <div className={classes.paperContent}>
                  <LoadingScreen status={urlLoading} noRadius text={s.newCheck.downloading} />
                  {source === "text" ? (
                    exclusionMode ? (
                      <>
                        <div
                          ref={exclusionDisplayRef}
                          className={classes.exclusionDisplay}
                          onMouseDown={handleMouseDown}
                          onMouseMove={handleMouseMove}
                          onMouseUp={handleSelect}
                          onKeyUp={handleSelect}
                          onScroll={drawExclusionOverlay}
                        >
                          {renderHighlightedText(localText, excludeRegions)}
                        </div>
                        <canvas ref={exclusionCanvasRef} className={classes.exclusionCanvas} />
                      </>
                    ) : (
                      <textarea ref={textareaRef}
                        className={classes.textarea}
                        placeholder={s.copyText}
                        autoFocus
                        value={localText}
                        onChange={(evt) => { onTextChange(evt.target.value) }}>
                      </textarea>
                    )
                  ) : (
                    <textarea className={classes.textarea}
                      value={localText}
                      onChange={(evt) => { onTextChange(evt.target.value) }}>
                    </textarea>
                  )}
                </div>
                <div className={classes.cancelButton} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {source === "text" && (
                      <>
                        <FormControlLabel
                          style={{ marginLeft: 0, marginRight: 4 }}
                          control={<Switch size="small" checked={exclusionMode} onChange={e => handleExclusionModeToggle(e.target.checked)} />}
                          label={<LabelWithTooltip label={s.newCheck.exclusionMode} tooltip={s.newCheck.exclusionHint} />}
                        />
                        {exclusionMode && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              disabled={!hasSelection}
                              onMouseDown={e => e.preventDefault()}
                              onClick={handleExcludeSelection}
                              style={{ marginRight: 4, textTransform: 'none', fontSize: 12 }}
                            >
                              {s.newCheck.excludeSelection}
                            </Button>
                            {/* Smart Exclusion Wizard — hidden, pending rollout */}
                            {false && <Tooltip title={s.newCheck.wizardButton}>
                              <IconButton size="small" style={{ marginRight: 8 }} onClick={openWizardMenu}>
                                <MagicWandIcon style={{ width: 20, height: 20, opacity: 0.54 }} />
                              </IconButton>
                            </Tooltip>}
                            {false && <Menu
                              anchorEl={wizardMenuAnchor}
                              open={Boolean(wizardMenuAnchor)}
                              onClose={() => setWizardMenuAnchor(null)}
                            >
                              <div style={{ position: 'relative' }}>
                                {s.newCheck.documentSections && Object.entries(s.newCheck.documentSections).map(([key, label]) => {
                                  const found = !wizardLoading && wizardSections.find(ws => ws.section === key);
                                  return (
                                    <MenuItem key={key} disabled={!found} onClick={found ? () => handleExcludeSection(found) : undefined}>
                                      {label}
                                    </MenuItem>
                                  );
                                })}
                                {wizardLoading && (
                                  <div style={{
                                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: 'rgba(255,255,255,0.6)'
                                  }}>
                                    <CircularProgress size={28} />
                                  </div>
                                )}
                              </div>
                            </Menu>}
                            {excludeRegions.length > 0 && (
                              <>
                                <span style={{ fontSize: 12, color: "#666", marginRight: 4 }}>
                                  {s.newCheck.excludedSummary.format(
                                    excludeRegions.reduce((c, r) => {
                                      const sl = localText.slice(r.start, r.end).trim();
                                      return c + (sl ? sl.split(/\s+/).length : 0);
                                    }, 0),
                                    excludeRegions.length
                                  )}
                                </span>
                                <Tooltip title={s.newCheck.clearExcludedRegions}>
                                  <IconButton size="small" onClick={handleClearExcludedRegions}>
                                    <DeleteSweepIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                  <IconButton onClick={clearTextField}>
                    <CancelIcon />
                  </IconButton>
                </div>
              </>
            }
          </>
        </>}
    </Paper>
  )
}